import { useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';

/**
 * Hook for checking user permissions.
 *
 * Reads the permissions array from the JWT (already in auth store).
 * Does NOT replace RLS — RLS still filters rows at the database level.
 * This controls what UI elements are visible and what actions are allowed.
 *
 * Usage:
 *   const { can, canAny, isAdmin } = usePermissions();
 *   if (can('inspections:delete')) { ... }
 *   <Can do="admin:access"><AdminPanel /></Can>
 */
export function usePermissions() {
  const user = useAuthStore((s) => s.user);
  const permissions = user?.permissions ?? [];
  const role = user?.role ?? '';
  const secondaryRoles = user?.secondaryRoles ?? [];
  const allRoles = [role, ...secondaryRoles];

  const can = useCallback(
    (permission: string): boolean => {
      if (!permissions.length) return false;
      // super_admin can do everything
      if (role === 'super_admin') return true;
      return permissions.includes(permission);
    },
    [permissions, role]
  );

  const canAny = useCallback(
    (...perms: string[]): boolean => perms.some((p) => can(p)),
    [can]
  );

  const canAll = useCallback(
    (...perms: string[]): boolean => perms.every((p) => can(p)),
    [can]
  );

  const hasRole = useCallback(
    (r: string): boolean => allRoles.includes(r),
    [allRoles]
  );

  return {
    can,
    canAny,
    canAll,
    hasRole,
    role,
    secondaryRoles,
    allRoles,
    isAdmin: allRoles.includes('super_admin') || allRoles.includes('admin'),
    isInspector: allRoles.includes('inspector'),
    isClient: allRoles.includes('client'),
    permissions,
  };
}
